import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

interface ISalesContractContextProps {
  propId?: string;
  tabName: string;
  open: boolean;
  refresh: number;
  alreadyOpened: boolean;
  onOpen: () => void;
  onClose: () => void;
  onRefresh: () => void;
  selectPropId: (value?: string) => void;
  selectTabName: (value: string) => void;
  onChangeAlreadyOpened: () => void;
}

const SalesContractContext = createContext<ISalesContractContextProps>({
  propId: undefined,
  tabName: "",
  open: false,
  refresh: 0,
  alreadyOpened: false,
  onRefresh: () => {},
  onOpen: () => {},
  onClose: () => {},
  selectPropId: () => {},
  selectTabName: () => {},
  onChangeAlreadyOpened: () => {},
});

interface ISalesContractProviderProps {
  children: ReactNode;
}

export const SalesContractProvider: React.FC<ISalesContractProviderProps> =
  memo(({ children }) => {
    const [propId, setPropId] = useState<string | undefined>();

    const [tabName, setTabName] = useState("");

    const [open, setOpen] = useState(false);

    const [refresh, setRefresh] = useState(0);

    const [alreadyOpened, setAlreadyOpened] = useState(false);

    const selectPropId = useCallback((value?: string) => {
      setPropId(value);
    }, []);

    const selectTabName = useCallback((value: string) => {
      setTabName(value);
    }, []);

    const onOpen = useCallback(() => {
      setOpen(true);
    }, []);

    const onClose = useCallback(() => {
      setOpen(false);
      setPropId(undefined);
      selectTabName("");
    }, [selectTabName]);

    const onRefresh = useCallback(() => {
      setRefresh((oldState) => (oldState += 1));
    }, []);

    const onChangeAlreadyOpened = useCallback(() => {
      setAlreadyOpened(true);
    }, []);

    const value = useMemo(() => {
      return {
        propId,
        tabName,
        open,
        refresh,
        alreadyOpened,
        onOpen,
        onClose,
        selectPropId,
        selectTabName,
        onRefresh,
        onChangeAlreadyOpened,
      };
    }, [
      propId,
      open,
      tabName,
      refresh,
      alreadyOpened,
      onOpen,
      onClose,
      selectPropId,
      selectTabName,
      onRefresh,
      onChangeAlreadyOpened,
    ]);
    return (
      <SalesContractContext.Provider value={value}>
        {children}
      </SalesContractContext.Provider>
    );
  });

export const useSalesContract = () => {
  const context = useContext(SalesContractContext);

  if (!context) {
    throw new Error("useSalesContract must be used with DashBoardProvider");
  }

  return context;
};
